import React, { useEffect, useState } from 'react';
import { X, Sparkles, Users, Copy, Check, Gift, Loader2, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';

const API = '/api';

/**
 * Referrals modal - shows the user's invite stats + one-tap copyable link.
 * Opened from the user dropdown in the Dashboard header.
 */
export default function ReferralsModal({ open, onClose, currentUser }) {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(false);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (!open) return;
        setLoading(true);
        fetch(`${API}/referrals/stats`, { credentials: 'include' })
            .then(r => r.ok ? r.json() : Promise.reject(new Error('Could not load referrals')))
            .then(setStats)
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [open]);

    if (!open) return null;

    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://brainsync.pro';
    const refLink = currentUser?.user_id ? `${origin}/?ref=${encodeURIComponent(currentUser.user_id)}` : '';

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(refLink);
            setCopied(true);
            toast.success('Referral link copied!');
            setTimeout(() => setCopied(false), 2000);
        } catch (_) {
            toast.error('Could not copy to clipboard');
        }
    };

    const shareNative = async () => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: 'BrainSync - your AI-powered bookmarks',
                    text: 'I\'ve been using BrainSync to save links and let AI summarize them. Join me - we both get +10 Premium AI saves.',
                    url: refLink,
                });
            } catch (_) { /* user cancelled */ }
        } else {
            copy();
        }
    };

    return (
        <div
            className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/60 backdrop-blur-sm"
            data-testid="referrals-modal"
            onClick={onClose}
        >
            <div
                className="relative w-full sm:max-w-md bg-background rounded-t-3xl sm:rounded-3xl border border-border shadow-2xl overflow-hidden max-h-[92vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center hover:bg-secondary transition-colors z-10"
                    data-testid="referrals-modal-close"
                >
                    <X className="w-4 h-4" />
                </button>

                <div className="px-6 pt-7 pb-4 bg-gradient-to-br from-emerald-50 via-white to-amber-50/40 border-b border-border">
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-emerald-500 text-white mb-3">
                        <Gift className="w-5 h-5" />
                    </div>
                    <h2 className="font-heading text-xl sm:text-2xl font-bold text-foreground mb-1.5" style={{ lineHeight: '1.25' }}>
                        Invite friends, grow your AI quota
                    </h2>
                    <p className="text-sm text-muted-foreground">
                        For every friend who signs up through your link, you <strong>both</strong> get{' '}
                        <span className="font-semibold text-emerald-700">+10 Premium AI saves</span> added to your quota. Forever.
                    </p>
                </div>

                {/* Stats */}
                <div className="px-6 py-5 grid grid-cols-2 gap-3 border-b border-border">
                    <div className="rounded-2xl border border-border bg-white p-4" data-testid="referrals-count-card">
                        <div className="flex items-center gap-2 mb-1.5">
                            <Users className="w-4 h-4 text-emerald-600" />
                            <span className="text-[10px] font-mono uppercase tracking-[0.15em] text-muted-foreground">Friends joined</span>
                        </div>
                        {loading ? (
                            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                        ) : (
                            <p className="text-3xl font-bold text-foreground leading-none">{stats?.referrals_count ?? 0}</p>
                        )}
                    </div>
                    <div className="rounded-2xl border border-border bg-white p-4" data-testid="referrals-bonus-card">
                        <div className="flex items-center gap-2 mb-1.5">
                            <Sparkles className="w-4 h-4 text-amber-500" />
                            <span className="text-[10px] font-mono uppercase tracking-[0.15em] text-muted-foreground">Bonus AI saves</span>
                        </div>
                        {loading ? (
                            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                        ) : (
                            <p className="text-3xl font-bold text-foreground leading-none">+{stats?.current_bonus_balance ?? 0}</p>
                        )}
                    </div>
                </div>

                {/* Invite link */}
                <div className="px-6 py-5">
                    <p className="text-xs font-medium text-muted-foreground mb-2">Your invite link</p>
                    <div className="flex items-stretch gap-2 mb-3">
                        <div className="flex-1 min-w-0 rounded-xl border border-border bg-secondary/40 px-3 py-2.5 flex items-center">
                            <span className="truncate text-xs text-foreground font-mono" data-testid="referrals-link-preview">
                                {refLink.replace(/^https?:\/\//, '')}
                            </span>
                        </div>
                        <Button
                            onClick={copy}
                            size="sm"
                            variant="outline"
                            className="rounded-xl shrink-0"
                            data-testid="referrals-copy-btn"
                        >
                            {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                        </Button>
                    </div>
                    <Button
                        onClick={shareNative}
                        className="w-full rounded-xl bg-primary text-white hover:bg-primary/90"
                        data-testid="referrals-share-btn"
                    >
                        <Share2 className="w-4 h-4 mr-1.5" />
                        Share link
                    </Button>
                </div>
            </div>
        </div>
    );
}
